import { DateTime } from 'luxon'
import { Driver } from 'store/drivers/drivers.type'
import { EquipmentResponse } from 'store/equipments/equipments.types'
import { AddressDto } from 'store/address/address.types'
import { Order, OrderEvent } from 'store/orders/orders.types'
import { CreateTripResponse, TripPayload } from 'store/trips/trips.types'
import {
  AceCommodity,
  AceShipment,
  AceTripFormValues,
  ManifestKind,
  buildManifestTripNumber,
  defaultAceTripFormValues,
  emptyCommodity,
  emptyParty,
  emptyShipment,
  emptyTrailer,
} from './ace-trip-form'

export type GeocodedLocation = {
  addressLine: string
  city: string
  stateProvince: string
  postalCode: string
  countryCode: string
}

export type LocatedStop = {
  task: TripPayload
  location: GeocodedLocation
}

export type CrossBorderShipmentPair = {
  kind: ManifestKind
  pickup: LocatedStop
  delivery: LocatedStop
}

export type BorderConnectPrefill = {
  manifestKind: ManifestKind
  formValues: AceTripFormValues
  scacCode: string
  carrierCode: string
  numericTripId: string
  shipmentsByKind: Record<ManifestKind, AceShipment[]>
}

export const normalizeCountryCode = (value?: string | null) => {
  const raw = (value ?? '').trim().toUpperCase()
  if (!raw) return ''
  if (raw === 'CA' || raw === 'CAN' || raw === 'CANADA') return 'CA'
  if (
    raw === 'US' ||
    raw === 'USA' ||
    raw === 'UNITED STATES' ||
    raw === 'UNITED STATES OF AMERICA'
  ) {
    return 'US'
  }
  return raw.slice(0, 2)
}

export const resolveManifestKind = (
  pickupCountry?: string | null,
  deliveryCountry?: string | null
): ManifestKind | null => {
  const pickup = normalizeCountryCode(pickupCountry)
  const delivery = normalizeCountryCode(deliveryCountry)
  if (pickup === 'CA' && delivery === 'US') return 'ACE'
  if (pickup === 'US' && delivery === 'CA') return 'ACI'
  return null
}

export const toAddressLine = (geo: AddressDto, fallbackAddress = '') => {
  const street = [geo.streetNumber, geo.streetName].filter(Boolean).join(' ').trim()
  if (street) return street
  const description = geo.description?.split(',')[0]?.trim()
  if (description) return description
  return fallbackAddress.split(',')[0]?.trim() ?? ''
}

export const toGeocodedLocation = (
  geo: AddressDto | undefined,
  fallbackAddress = ''
): GeocodedLocation => ({
  addressLine: geo ? toAddressLine(geo, fallbackAddress) : fallbackAddress.split(',')[0]?.trim() ?? '',
  city: geo?.city?.trim() ?? '',
  stateProvince: (geo?.stateCode || geo?.state || '').trim(),
  postalCode: geo?.zipCode?.trim() ?? '',
  countryCode: normalizeCountryCode(geo?.countryCode || geo?.Country),
})

const toDateInputValue = (value?: string | null) => {
  if (!value) return ''
  const iso = DateTime.fromISO(value)
  if (iso.isValid) return iso.toFormat('yyyy-MM-dd')
  const dateOnly = DateTime.fromFormat(value.slice(0, 10), 'yyyy-MM-dd')
  if (dateOnly.isValid) return dateOnly.toFormat('yyyy-MM-dd')
  return ''
}

const toDateTimeLocalValue = (value?: string | null) => {
  if (!value) return ''
  const dt = DateTime.fromISO(value)
  if (!dt.isValid) return ''
  return dt.toFormat("yyyy-MM-dd'T'HH:mm")
}

const toGender = (sex?: string | null) => {
  const value = (sex ?? '').trim().toUpperCase()
  if (value.startsWith('F')) return 'F'
  if (value.startsWith('M')) return 'M'
  return ''
}

export const resolveNumericTripId = (created?: CreateTripResponse | null) => {
  if (created?.tripId != null) return String(created.tripId)
  const digits = (created?.userTripId ?? '').replace(/\D/g, '')
  return digits || '0'
}

export const getShipmentOrderKey = (task: TripPayload) => {
  if (task.sourceOrderPkId != null) return `order:${task.sourceOrderPkId}`
  if (task.orderId) return `load:${task.orderId}`
  if (task.orderEventId != null) return `event:${task.orderEventId}`
  return `seq:${task.sequenceId}`
}

export const getPickupAndDeliveryTasks = (tasks: TripPayload[]) =>
  [...tasks]
    .filter((task) => task.type === 'PICKUP' || task.type === 'DELIVERY')
    .sort((a, b) => a.sequenceId - b.sequenceId)

const findOrderForTask = (task: TripPayload, orders: Order[]) =>
  orders.find(
    (item) =>
      (task.sourceOrderPkId != null && item.orderId === task.sourceOrderPkId) ||
      (!!task.orderId && item.userOrderId === task.orderId)
  )

export const findTaskOrderEvent = (
  task: TripPayload | undefined,
  orders: Order[]
): OrderEvent | undefined => {
  if (!task) return undefined
  const order = findOrderForTask(task, orders)
  const events = [...(order?.orderEvents ?? []), ...(order?.exceptions ?? [])]
  if (task.orderEventId != null) {
    const matched = events.find((event) => event.orderEventId === task.orderEventId)
    if (matched) return matched
  }
  const eventType = task.type === 'DELIVERY' ? 'DELIVERY' : 'PICKUP'
  return events.find((event) => event.eventType === eventType)
}

export const findPickupOrderEvent = (
  pickup: TripPayload | undefined,
  orders: Order[]
): OrderEvent | undefined => findTaskOrderEvent(pickup, orders)

const toCommodityWeight = (value?: number | string | null) => {
  if (value == null || value === '') return ''
  const asNumber = Number(value)
  if (Number.isFinite(asNumber) && asNumber <= 0) return ''
  return String(value)
}

const toCommodityQuantity = (value?: number | string | null) => {
  if (value == null || value === '') return ''
  const asNumber = Number(value)
  if (Number.isFinite(asNumber) && asNumber <= 0) return ''
  return String(value)
}

export const commodityFromPickupEvent = (
  pickupEvent: OrderEvent | undefined,
  trailerNumber: string
): AceCommodity => ({
  ...emptyCommodity(),
  description: (pickupEvent?.notes?.trim() || pickupEvent?.pickupNumber?.trim() || '').slice(0, 450),
  quantity: toCommodityQuantity(pickupEvent?.loadCount),
  weight: toCommodityWeight(pickupEvent?.weightValue),
  weightUnit: 'L',
  loadedOn: trailerNumber,
})

const findTractor = (
  tasks: TripPayload[],
  equipments: EquipmentResponse[]
) => {
  const hook = tasks.find((task) => task.type === 'HOOK_TRACTOR' && task.tractorId)
  const tractorId = hook?.tractorId ?? tasks.find((task) => task.tractorId)?.tractorId
  if (!tractorId) return undefined
  return equipments.find((item) => String(item.equipmentId) === String(tractorId))
}

const findTrailer = (
  tasks: TripPayload[],
  equipments: EquipmentResponse[]
) => {
  const hook = tasks.find((task) => task.type === 'HOOK_TRAILER' && task.trailerId)
  const trailerId = hook?.trailerId ?? tasks.find((task) => task.trailerId)?.trailerId
  if (!trailerId) return undefined
  return equipments.find((item) => String(item.equipmentId) === String(trailerId))
}

const pairStops = (
  pickups: LocatedStop[],
  deliveries: LocatedStop[],
  kind: ManifestKind
): CrossBorderShipmentPair[] => {
  if (!pickups.length || !deliveries.length) return []
  if (pickups.length === 1) {
    return deliveries.map((delivery) => ({ kind, pickup: pickups[0], delivery }))
  }
  if (deliveries.length === 1) {
    return pickups.map((pickup) => ({ kind, pickup, delivery: deliveries[0] }))
  }
  const count = Math.max(pickups.length, deliveries.length)
  return Array.from({ length: count }, (_, index) => ({
    kind,
    pickup: pickups[Math.min(index, pickups.length - 1)],
    delivery: deliveries[Math.min(index, deliveries.length - 1)],
  }))
}

export const buildCrossBorderShipmentPairs = (
  tasks: TripPayload[],
  geoByAddress: Record<string, AddressDto | undefined>
): CrossBorderShipmentPair[] => {
  const stops: LocatedStop[] = getPickupAndDeliveryTasks(tasks).map((task) => ({
    task,
    location: toGeocodedLocation(geoByAddress[task.address], task.address ?? ''),
  }))

  const groups = new Map<string, LocatedStop[]>()
  for (const stop of stops) {
    const key = getShipmentOrderKey(stop.task)
    const group = groups.get(key)
    if (group) group.push(stop)
    else groups.set(key, [stop])
  }

  const pairs: CrossBorderShipmentPair[] = []
  Array.from(groups.values()).forEach((group) => {
    const pickups = group.filter((stop) => stop.task.type === 'PICKUP')
    const deliveries = group.filter((stop) => stop.task.type === 'DELIVERY')
    const acePickups = pickups.filter((stop) => stop.location.countryCode === 'CA')
    const aceDeliveries = deliveries.filter((stop) => stop.location.countryCode === 'US')
    const aciPickups = pickups.filter((stop) => stop.location.countryCode === 'US')
    const aciDeliveries = deliveries.filter((stop) => stop.location.countryCode === 'CA')
    pairs.push(...pairStops(acePickups, aceDeliveries, 'ACE'))
    pairs.push(...pairStops(aciPickups, aciDeliveries, 'ACI'))
  })
  return pairs
}

export const resolvePreferredManifestKind = (
  pairs: CrossBorderShipmentPair[]
): ManifestKind | null => {
  const aceCount = pairs.filter((pair) => pair.kind === 'ACE').length
  const aciCount = pairs.filter((pair) => pair.kind === 'ACI').length
  if (aceCount === 0 && aciCount === 0) return null
  if (aciCount > aceCount) return 'ACI'
  return 'ACE'
}

const toParty = (stop: LocatedStop) => ({
  ...emptyParty(),
  name: stop.task.name ?? '',
  address: stop.location.addressLine,
  city: stop.location.city,
  stateProvince: stop.location.stateProvince,
  postalCode: stop.location.postalCode,
})

export const buildShipmentsFromPairs = (
  pairs: CrossBorderShipmentPair[],
  kind: ManifestKind,
  trailerNumber: string,
  orders: Order[]
): AceShipment[] => {
  const matched = pairs.filter((pair) => pair.kind === kind)
  if (!matched.length) return []

  return matched.map((pair) => {
    const pickupEvent = findTaskOrderEvent(pair.pickup.task, orders)
    return {
      ...emptyShipment(),
      shipmentType: kind === 'ACE' ? 'PAPS' : 'PARS',
      shipmentControlNumber: '',
      provinceOfLoading: pair.pickup.location.stateProvince,
      shipper: toParty(pair.pickup),
      consignee: toParty(pair.delivery),
      commodities: [commodityFromPickupEvent(pickupEvent, trailerNumber)],
    }
  })
}

type BuildPrefillArgs = {
  manifestKind: ManifestKind
  scacCode: string
  carrierCode: string
  numericTripId: string
  tripTasks: TripPayload[]
  driver?: Driver | null
  equipments: EquipmentResponse[]
  geoByAddress: Record<string, AddressDto | undefined>
  orders: Order[]
  pairs?: CrossBorderShipmentPair[]
}

export const buildBorderConnectPrefill = ({
  manifestKind,
  scacCode,
  carrierCode,
  numericTripId,
  tripTasks,
  driver,
  equipments,
  geoByAddress,
  orders,
  pairs: providedPairs,
}: BuildPrefillArgs): BorderConnectPrefill => {
  const tractor = findTractor(tripTasks, equipments)
  const trailer = findTrailer(tripTasks, equipments)
  const pairs = providedPairs ?? buildCrossBorderShipmentPairs(tripTasks, geoByAddress)
  const tripNumber = buildManifestTripNumber(
    manifestKind,
    scacCode,
    carrierCode,
    numericTripId
  )
  const trailerNumber = trailer?.unitNumber ?? ''
  const shipmentsByKind: Record<ManifestKind, AceShipment[]> = {
    ACE: buildShipmentsFromPairs(pairs, 'ACE', trailerNumber, orders),
    ACI: buildShipmentsFromPairs(pairs, 'ACI', trailerNumber, orders),
  }
  const shipments = shipmentsByKind[manifestKind].length
    ? shipmentsByKind[manifestKind]
    : [
        {
          ...emptyShipment(),
          shipmentType: manifestKind === 'ACE' ? 'PAPS' : 'PARS',
        },
      ]
  const arrivalTimes = pairs
    .filter((pair) => pair.kind === manifestKind)
    .map((pair) => pair.delivery.task.startTime)
    .filter(Boolean)
    .sort()

  const formValues: AceTripFormValues = {
    ...defaultAceTripFormValues,
    manifestKind,
    tripNumber,
    estimatedArrival: toDateTimeLocalValue(
      arrivalTimes[0] ||
        pairs.find((pair) => pair.kind === manifestKind)?.pickup.task.startTime
    ),
    truckNumber: tractor?.unitNumber ?? '',
    truckType: tractor?.equipmentTypeCode ?? 'TR',
    vinNumber: tractor?.vinNumber ?? '',
    truckLicensePlateNumber: tractor?.licensePlateNumber ?? '',
    truckLicensePlateState: tractor?.stateProvince ?? '',
    trailer: {
      ...emptyTrailer(),
      trailerNumber,
      trailerType: trailer?.equipmentTypeCode ?? 'TL',
      licensePlateNumber: trailer?.licensePlateNumber ?? '',
      licensePlateState: trailer?.stateProvince ?? '',
    },
    driverFirstName: driver?.firstName ?? '',
    driverLastName: driver?.lastName ?? '',
    gender: toGender(driver?.sex),
    dateOfBirth: toDateInputValue(driver?.dateOfBirth),
    citizenshipCountry: driver?.citizenshipCountry ?? '',
    fastCardNumber: driver?.fastCardNumber ?? '',
    shipments,
  }

  return {
    manifestKind,
    formValues,
    scacCode,
    carrierCode,
    numericTripId,
    shipmentsByKind,
  }
}
